import type { Diagnostic } from "@codemirror/lint";
import type { EditorView } from "@codemirror/view";
import { PythonSyntaxError, translatePython } from "../util/pythonTranspiler";

const KAREL_GLOBALS = [
  "move",
  "turnLeft",
  "turnRight",
  "turnAround",
  "putBeeper",
  "pickBeeper",
  "paintCorner",
  "random",
  "randomSeed",
  "frontIsClear",
  "frontIsBlocked",
  "leftIsClear",
  "leftIsBlocked",
  "rightIsClear",
  "rightIsBlocked",
  "beepersPresent",
  "noBeepersPresent",
  "canMove",
  "karelAvenue",
  "karelStreet",
  "facingNorth",
  "notFacingNorth",
  "facingEast",
  "notFacingEast",
  "facingSouth",
  "notFacingSouth",
  "facingWest",
  "notFacingWest",
  "print",
  "input",
  "len",
  "length",
  "int",
  "float",
  "str",
  "repeat",
  "main",
];

/** Snake-case aliases exposed to Python programs by the eval engine. */
const KAREL_PYTHON_GLOBALS = [
  "move",
  "turn_left",
  "turn_right",
  "turn_around",
  "put_beeper",
  "pick_beeper",
  "paint_corner",
  "random",
  "random_seed",
  "front_is_clear",
  "front_is_blocked",
  "left_is_clear",
  "left_is_blocked",
  "right_is_clear",
  "right_is_blocked",
  "beepers_present",
  "no_beepers_present",
  "can_move",
  "karel_avenue",
  "karel_street",
  "facing_north",
  "not_facing_north",
  "facing_east",
  "not_facing_east",
  "facing_south",
  "not_facing_south",
  "facing_west",
  "not_facing_west",
];

const PYTHON_BUILTINS = new Set([
  "range",
  "print",
  "len",
  "int",
  "str",
  "float",
  "bool",
  "abs",
  "min",
  "max",
  "sum",
  "round",
  "list",
  "dict",
  "tuple",
  "set",
  "enumerate",
  "zip",
  "map",
  "filter",
  "input",
]);

const PYTHON_KEYWORDS = new Set([
  "if",
  "elif",
  "else",
  "while",
  "for",
  "in",
  "def",
  "return",
  "and",
  "or",
  "not",
  "pass",
  "break",
  "continue",
  "True",
  "False",
  "None",
]);

function lineStartOffsets(text: string): number[] {
  const offsets = [0];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "\n") offsets.push(i + 1);
  }
  return offsets;
}

function offsetAt(offsets: number[], line: number, ch: number): number {
  const index = Math.max(0, Math.min(line - 1, offsets.length - 1));
  return offsets[index] + Math.max(0, ch - 1);
}

function stripPythonComment(line: string): string {
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === "'" && !inDouble) inSingle = !inSingle;
    else if (ch === '"' && !inSingle) inDouble = !inDouble;
    else if (ch === "#" && !inSingle && !inDouble) return line.slice(0, i);
  }
  return line;
}

/**
 * Flags calls to unknown function names in Python source. This catches typos
 * such as `movee()` that the transpiler itself cannot detect. Only call-like
 * identifiers are checked, so ordinary variable assignments are unaffected.
 */
function pythonUndefinedCallDiagnostics(code: string): Diagnostic[] {
  const defined = new Set<string>();
  for (const match of code.matchAll(/\bdef\s+([A-Za-z_]\w*)\s*\(/g)) {
    defined.add(match[1]);
  }

  const known = new Set([
    ...KAREL_GLOBALS,
    ...KAREL_PYTHON_GLOBALS,
    ...PYTHON_BUILTINS,
  ]);

  const offsets = lineStartOffsets(code);
  const lines = code.split("\n");
  const diagnostics: Diagnostic[] = [];
  const seen = new Set<string>();
  const callPattern = /([A-Za-z_]\w*)\s*\(/g;

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const lineStart = offsets[lineIndex] ?? 0;
    const stripped = stripPythonComment(lines[lineIndex]);
    let match: RegExpExecArray | null;
    callPattern.lastIndex = 0;
    while ((match = callPattern.exec(stripped)) !== null) {
      const name = match[1];
      const preceding = stripped[match.index - 1];
      if (preceding === "." || preceding === '"' || preceding === "'") continue;
      if (PYTHON_KEYWORDS.has(name) || known.has(name) || defined.has(name))
        continue;
      const from = lineStart + match.index;
      const key = name + "@" + from;
      if (seen.has(key)) continue;
      seen.add(key);
      diagnostics.push({
        from,
        to: from + name.length,
        severity: "error",
        message: `Unknown function '${name}'`,
      });
    }
  }

  return diagnostics;
}

export async function jsLinter(view: EditorView): Promise<Diagnostic[]> {
  const code = view.state.doc.toString();
  if (code.trim() === "") return [];
  const { JSHINT } = await import("jshint");
  JSHINT(code, {
    esversion: 11,
    asi: true,
    undef: true,
    unused: false,
    loopfunc: true,
    predef: KAREL_GLOBALS,
  });
  const errors = JSHINT.errors ?? [];
  const offsets = lineStartOffsets(code);
  const diagnostics: Diagnostic[] = [];
  for (const error of errors) {
    if (!error) continue;
    const line = error.line ?? 1;
    const ch = error.character ?? 1;
    const from = offsetAt(offsets, line, ch);
    const lineEnd = offsets[line] ?? code.length;
    diagnostics.push({
      from,
      to: Math.max(from + 1, lineEnd),
      severity: "error",
      message: error.reason ?? "Syntax error",
    });
  }
  return diagnostics;
}

export function pythonLinter(view: EditorView): Diagnostic[] {
  const code = view.state.doc.toString();
  if (code.trim() === "") return [];
  try {
    translatePython(code);
    return pythonUndefinedCallDiagnostics(code);
  } catch (error) {
    if (error instanceof PythonSyntaxError) {
      const lines = code.split("\n");
      const lineIndex = Math.max(0, Math.min(error.line - 1, lines.length - 1));
      const starts = lineStartOffsets(code);
      const from = starts[lineIndex] ?? 0;
      const to = from + (lines[lineIndex]?.length ?? 1);
      return [
        {
          from,
          to: Math.max(from + 1, to),
          severity: "error",
          message: error.message,
        },
      ];
    }
    return [
      {
        from: 0,
        to: Math.min(1, code.length),
        severity: "error",
        message: "Could not parse Python program",
      },
    ];
  }
}
